import React, { useEffect, useState } from "react";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import {
  Breadcrumbs,
  Button,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Paper,
  Table,
  TableContainer,
  Typography,
  IconButton,
  Tooltip,
  Link,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box } from "@mui/material";
import { Container } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Loading } from "../../../components/Loading";
import { useNavigate } from "react-router-dom";
import AddMechanicModal from "./AddMechanicModal";
import axios from "axios";
import { useToast } from "../../../components/Toast";
import NavigationHeader from "../../../components/NavigationHeader";
import { tableHeaderStyles } from "../../../utils/styles";
import { tableCellStyles } from "../../../utils/styles";

interface Mechanic {
  id?: string;
  name: string;
  phoneNumber: string;
  location?: string;
  companyId?: string;
  idType?: string;
  idNumber?: string;
}

const Mechanics = () => {
  const [mechanics, setMechanics] = useState<Mechanic[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMechanic, setSelectedMechanic] = useState<
    Mechanic | undefined
  >();
  const { user, loading: authLoading } = useFirebaseAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    if (user && !authLoading) {
      fetchMechanicData();
    }
  }, [user, authLoading]);

  const fetchMechanicData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}master-data/mechanics`,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      if (response?.data?.isSuccess) {
        setMechanics(response?.data?.data);
      }
    } catch (error) {
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMechanic = () => {
    setSelectedMechanic(undefined);
    setIsModalOpen(true);
  };

  const handleEditMechanic = (mechanic: Mechanic) => {
    setSelectedMechanic(mechanic);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedMechanic(undefined);
  };

  const handleSubmit = async (mechanicData: any) => {
    setIsSubmitting(true);
    if (selectedMechanic) {
      // Update existing mechanic
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_HOST}master-data/mechanics/${mechanicData?.id}`,
          mechanicData,
          {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          showToast("Mechanic updated successfully", "success");
          fetchMechanicData();
        } else {
          showToast(`Error updating mechanic`, "error");
        }
      } catch (error) {
        console.error("Error updating mechanic", error);
        showToast(`Error updating mechanic`, "error");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Add new mechanic
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_HOST}master-data/mechanics`,
          mechanicData,
          {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          showToast("Mechanic added successfully", "success");
          fetchMechanicData();
        } else {
          showToast(`Error submitting data`, "error");
        }
      } catch (error) {
        console.error("Error adding mechanic", error);
        showToast(`Error adding mechanic`, "error");
      } finally {
        setIsSubmitting(false);
      }
    }
    handleModalClose();
  };

  if (isLoading || authLoading || isSubmitting) {
    return isSubmitting ? (
      <Loading message="Submitting..." fullScreen={true} />
    ) : (
      <Loading message="Loading mechanics..." fullScreen={true} />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Mechanics" },
        ]}
        title="Mechanics"
        button={{
          label: "Add Mechanic",
          onClick: handleAddMechanic,
          icon: <AddIcon />,
        }}
      />

      {mechanics.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
          No mechanics found
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="mechanics table">
            <TableHead>
              <TableRow>
                <TableCell {...tableHeaderStyles}>Name</TableCell>
                <TableCell {...tableHeaderStyles}>Contact Details</TableCell>
                <TableCell {...tableHeaderStyles}>ID Proof</TableCell>
                <TableCell {...tableHeaderStyles}>ID Proof Number</TableCell>
                <TableCell {...tableHeaderStyles}>Address</TableCell>
                <TableCell {...tableHeaderStyles} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mechanics.map((mechanic) => (
                <TableRow
                  key={mechanic.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <TableCell {...tableCellStyles}>{mechanic.name}</TableCell>
                  <TableCell {...tableCellStyles}>
                    {mechanic.phoneNumber || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {mechanic.idType || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {mechanic.idNumber || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {mechanic.location || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles} align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditMechanic(mechanic)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddMechanicModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        mechanic={selectedMechanic}
        isEdit={!!selectedMechanic}
      />
    </Container>
  );
};

export default Mechanics;
